import { ProLayout } from '@ant-design/pro-components';
import type { MenuDataItem } from '@ant-design/pro-components';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '@/store';
import { useEffect, useMemo } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import AppHeader from './AppHeader';
import NProgress from 'nprogress';
import {
  PageEnum,
  SUPERUSER,
  USER_MANAG,
  EIMIT,
  ISLOGIN,
  COLLAPS,
} from '@/enums/pageEnum';
import { MenuPath } from '@/facilities/model/user';
import { setCollaps } from '@/store/modules/app';
import menu from '@/router/menu';
import { useSize } from 'ahooks';
import { parmsFromat } from '@/utils/toolt';

function Layout() {
  const { pathname } = useLocation();
  const navigation = useNavigate();
  const dispatch = useAppDispatch();
  const { menus, roles, role, isLogin, collaps, theme } = useAppSelector(
    (state) => state.app,
  );
  const size = useSize(document.body);
  const { t } = useTranslation();
  const menuItemRender = (item: MenuDataItem, dom: ReactNode) => {
    return (
      <Link
        to={item.path!}
        onClick={() => {
          document.dispatchEvent(new CustomEvent(EIMIT, { detail: item }));
        }}
      >
        {dom}
      </Link>
    );
  };

  const currentMenus = useMemo(() => {
    const routes = menus?.routes?.map((item) => {
      const { path, defaultParams, routes, name } = item;
      const rout = {
        ...item,
        path: parmsFromat(defaultParams, path),
        routes: routes?.map((li) => ({
          ...li,
          path: parmsFromat(li.defaultParams, li.path),
          name: t(li.name!),
        })),
        icon: menu.routes?.find((li) => li.name === name)?.icon,
        name: t(name!),
      };
      if (SUPERUSER === role && name === USER_MANAG && routes) {
        return { ...rout, routes: [] };
      }
      return rout;
    });
    return { path: '/', routes: routes };
  }, [menus, role, t]);

  const getPathWithRoute = (routes: MenuPath[]) => {
    return routes.find((item: MenuPath) => item.path === pathname);
  };

  useEffect(() => {
    if (!roles.length && !isLogin) {
      navigation(PageEnum.BASE_LOGIN, { replace: true });
    }
  }, [roles, isLogin, navigation]);

  const getPrint = useMemo(() => {
    const parms = new window.URL(decodeURIComponent(window.location.href));
    return parms && parms.searchParams.get('print') === 'true';
  }, []);

  const getLayoutToken = useMemo(() => {
    return {
      sider: {
        colorMenuBackground: theme === 'dark' ? '#141414' : '#181D31',
        colorTextMenuSelected: '#fff',
        colorTextMenu: '#ADB3BC',
        colorTextMenuItemHover: '#fff',
        colorBgMenuItemSelected: '#0364FF',
      },
      bgLayout: theme === 'dark' ? '#000' : '#F4F4F4',
      header: {
        heightLayoutHeader: 60,
        colorBgHeader: '#fff',
      },
    };
  }, [theme]);

  useEffect(() => {
    if (localStorage.getItem(COLLAPS)) return;
    dispatch(setCollaps(size && size?.width < 1280 ? true : false));
  }, [size, dispatch]);

  if (getPrint) {
    return <Outlet />;
  }

  return (
    <ProLayout
      title={false}
      layout="mix"
      location={{ pathname }}
      route={currentMenus}
      fixedHeader
      collapsed={collaps}
      className="menuLayout"
      subMenuItemRender={menuItemRender}
      menuItemRender={menuItemRender}
      onPageChange={() => {
        const isLog = localStorage.getItem(ISLOGIN) === 'true';
        if (!isLog) {
          navigation(PageEnum.BASE_LOGIN, { replace: true });
        }
        NProgress.start();
      }}
      pageTitleRender={() => {
        let cuPath: MenuPath | undefined;
        if (menu.routes) {
          menu?.routes.some((item: MenuPath) => {
            if (pathname.includes(item.path)) {
              cuPath =
                item.path === pathname
                  ? item
                  : getPathWithRoute(item.routes || item.child || []);
              return cuPath;
            }
          });
        }
        return `${t(cuPath?.name || 'Fusion')} - Fusion TMS`;
      }}
      headerRender={() => (
        <>
          <AppHeader
            collapsed={collaps}
            onCollapse={() => {
              localStorage.setItem(COLLAPS, JSON.stringify(!collaps));
              dispatch(setCollaps(!collaps));
            }}
          />
        </>
      )}
      token={getLayoutToken}
      collapsedButtonRender={() => <></>}
      contentStyle={{ paddingBlock: 0, paddingInline: 0 }}
    >
      <Outlet />
    </ProLayout>
  );
}

export default Layout;
